import { CaretDown } from "@phosphor-icons/react";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames/bind";
import React, { useCallback, useEffect, useState } from "react";
import { Icon, IconLoading } from "../Icon";
import { Skeleton } from "../Skeleton";
import styles from "./Accordion.module.scss";
import type { AccordionPanelProps } from "./types";

const cx = classNames.bind(styles);

/**
 * The accordion component delivers large amounts of content in a small space through progressive disclosure.
 */
const AccordionPanel = ({
  dataKey,
  title,
  isOpen,
  className,
  children,
  onOpen,
  onClose,
  actionRow,
  error,
  contentClassName,
  shouldUseAnimation = true,
  iconVariant,
  iconProps = {},
  variant = "default",
  isLoading = false,
  isBusy = false,
}: AccordionPanelProps) => {
  const [contentRef, setContentRef] = useState<HTMLElement | null>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const [animatedstyle, animate] = useSpring(
    () => ({ config: { duration: 150 }, height: "0px" }),
    []
  );

  const observer = React.useRef<ResizeObserver>(null);

  if (observer.current === null) {
    // @ts-expect-error - Generally, rewriting current in this fashion is not allowed, but there are specific cases where it is proper (https://beta.reactjs.org/apis/react/useRef#avoiding-recreating-the-ref-contents)
    observer.current = new ResizeObserver((entries) => {
      if (entries?.[0]) {
        setContentHeight((entries[0].target as HTMLElement).offsetHeight);
      }
    });
  }

  const handleContentRef = useCallback((node) => {
    if (node && observer?.current) {
      setContentRef(node);
      setContentHeight(node.offsetHeight);
      observer.current.observe(node);
    }
  }, []);

  const onClick = () => {
    if (isOpen && onClose) {
      onClose(dataKey);
    } else {
      if (onOpen) {
        onOpen(dataKey);
      }
    }
  };

  useEffect(() => {
    if (!shouldUseAnimation) return;
    if (isOpen) {
      contentRef?.removeAttribute("hidden");
      contentRef?.removeAttribute("inert");

      animate({ height: `${contentHeight}px` });
    } else {
      contentRef?.setAttribute("hidden", "");
      contentRef?.setAttribute("inert", "");

      animate({ height: "0px" });
    }
  }, [isOpen, contentHeight, contentRef, shouldUseAnimation]);

  return (
    <Skeleton
      isLoading={isLoading}
      isBusy={isBusy}
    >
      <div
        className={cx("panel", variant && `is-${variant}`, className)}
        data-state={isOpen ? "open" : "closed"}
      >
        <button
          id={`${dataKey}-header`}
          className={cx("titleContainer", variant && `is-${variant}`)}
          onClick={onClick}
          aria-expanded={isOpen}
          aria-controls={`${dataKey}-section`}
          type="button"
          data-role="accordion-trigger"
        >
          <div className={cx("iconContainer", { isOpen })}>
            {iconVariant ? (
              <Icon
                variant={iconVariant}
                // pnpm resolves @types/react to separate instances for consumers (Radix-augmented)
                // and this package (vanilla @types/react@17.0.93), making CSSProperties in
                // iconProps structurally incompatible with Icon's props at the index-signature
                // level. The cast is safe at runtime.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {...(iconProps as any)}
              />
            ) : (
              <CaretDown
                size={22}
                weight="thin"
              />
            )}
          </div>
          <div className={cx("title", { hasError: error })}>
            {title}
            {isBusy ? (
              <IconLoading
                speed="fast"
                style={{ marginLeft: 4 }}
              />
            ) : null}
          </div>

          {actionRow ? (
            <div
              className={cx("actionRow")}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              {Array.isArray(actionRow) ? (
                <>{actionRow.map((action) => action)}</>
              ) : (
                actionRow
              )}
            </div>
          ) : null}
        </button>

        <animated.div
          id={`${dataKey}-section`}
          role="region"
          className={cx("content", {
            isOpen,
          })}
          aria-labelledby={`${dataKey}-section`}
          style={
            shouldUseAnimation
              ? { ...animatedstyle }
              : { height: isOpen ? `${contentHeight}px` : "0px" }
          }
        >
          <div
            ref={handleContentRef}
            className={contentClassName}
          >
            {children}
          </div>
        </animated.div>
      </div>
    </Skeleton>
  );
};

export default AccordionPanel;
